import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ErrorPage } from '@/components/error-page';
import { captureException, captureFrontendError } from '@/lib/sentry';
import { UnexpectedError } from '@hookmyapp/observability/errors';

export interface ErrorInfo {
  message: string;
  url?: string;
  status?: number;
}

interface ErrorContextValue {
  hasError: boolean;
  showErrorPage: (error: ErrorInfo) => void;
  resetError: () => void;
}

const ErrorContext = createContext<ErrorContextValue | null>(null);

// Module-level function for use outside React tree (e.g., WorkspaceContext's authFetch)
let globalShowErrorPage: ((error: ErrorInfo) => void) | null = null;

export function triggerErrorPage(error: ErrorInfo) {
  if (globalShowErrorPage) {
    globalShowErrorPage(error);
  }
}

export function ErrorProvider({ children }: { children: React.ReactNode }) {
  const [errorState, setErrorState] = useState<{ hasError: boolean; error: ErrorInfo | null }>({
    hasError: false,
    error: null,
  });

  const showErrorPage = useCallback((error: ErrorInfo) => {
    // Severity keyed on status (AIT-335): a 5xx means the server failed a
    // waiting human → sev1 (use-fetch's 5xx path has no other capture site).
    // No status = the request never got a response (network blip, aborted
    // fetch) → sev2; blanket sev1 here paged on every client-side blip.
    const err = new Error(error.message);
    if (error.status !== undefined && error.status >= 500) {
      captureException(err, { url: error.url, status: error.status });
    } else {
      captureFrontendError(err, { url: error.url, status: error.status });
    }
    setErrorState({ hasError: true, error });
  }, []);

  // Keep a ref for stable identity in consumers
  const showErrorPageRef = useRef(showErrorPage);
  useEffect(() => {
    showErrorPageRef.current = showErrorPage;
  }, [showErrorPage]);

  // Expose to module-level triggerErrorPage
  useEffect(() => {
    globalShowErrorPage = (error: ErrorInfo) => showErrorPageRef.current(error);
    return () => {
      globalShowErrorPage = null;
    };
  }, []);

  const resetError = useCallback(() => {
    setErrorState({ hasError: false, error: null });
  }, []);

  if (errorState.hasError) {
    return <ErrorPage />;
  }

  return (
    <ErrorContext.Provider value={{ hasError: errorState.hasError, showErrorPage, resetError }}>
      {children}
    </ErrorContext.Provider>
  );
}

export function useError(): ErrorContextValue {
  const ctx = useContext(ErrorContext);
  if (!ctx) {
    throw new UnexpectedError({
      message: 'useError must be used within an ErrorProvider',
      code: 'USE_ERROR_OUTSIDE_PROVIDER',
    });
  }
  return ctx;
}
